// Administração > Módulos > Envio de Faturas pelo WhatsApp (pedido
// 2026-10-12): lembrete antes do vencimento, no dia e cobranças depois de
// vencida, nos dias e horários escolhidos. Backend:
// gpsdrive-backend src/invoice-reminders/ (GET/PUT /admin/invoice-reminders,
// /test, /preview, /history). As faturas vêm do espelho do Asaas do menu
// Financeiro; o envio confere no Asaas na hora se ainda está em aberto.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Eye, Loader2, Plus, Send, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import {
  GpsDriveApiError,
  gpsDriveGet,
  gpsDrivePost,
  gpsDrivePut,
} from "@/lib/api/gpsdrive-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/invoice-whatsapp")({
  component: () => (
    <AdminAccessGate action="notificacoes">
      <InvoiceWhatsappAdminPage />
    </AdminAccessGate>
  ),
  head: () => ({
    meta: [{ title: "Envio de Faturas pelo WhatsApp — Administração — Wp Rastreamento" }],
  }),
});

type Kind = "before" | "due" | "after";

interface Settings {
  enabled: boolean;
  beforeDays: number[];
  sendOnDueDay: boolean;
  afterMaxSends: number;
  afterIntervalDays: number;
  sendDays: number[];
  sendTimes: string[];
  messageDelaySeconds: number;
  beforeTemplate: string;
  dueTemplate: string;
  afterTemplate: string;
  whatsappConnected: boolean;
  asaasConnected: boolean;
  variables: { key: string; label: string }[];
  defaults: Record<Kind, string>;
}

type Form = Omit<Settings, "whatsappConnected" | "asaasConnected" | "variables" | "defaults">;

interface Preview {
  total: number;
  withoutPhone: number;
  byKind: Partial<Record<Kind, number>>;
  items: {
    paymentId: string;
    customerName: string;
    phone: string | null;
    value: number;
    dueDate: string;
    stage: string;
    kind: Kind;
  }[];
}

interface HistoryRow {
  id: string;
  stage: string;
  status: "sent" | "failed" | "no_phone";
  customerName: string | null;
  phone: string | null;
  value: number | null;
  dueDate: string | null;
  errorMessage: string | null;
  sentAt: string;
}

const API = "/api/v1/admin/invoice-reminders";

const WEEKDAYS = [
  { value: 1, short: "Seg" },
  { value: 2, short: "Ter" },
  { value: 3, short: "Qua" },
  { value: 4, short: "Qui" },
  { value: 5, short: "Sex" },
  { value: 6, short: "Sáb" },
  { value: 0, short: "Dom" },
];
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const KIND_LABEL: Record<Kind, string> = {
  before: "Antes do vencimento",
  due: "No dia do vencimento",
  after: "Depois de vencida",
};
const SAMPLE: Record<string, string> = {
  cliente: "Maria Souza",
  valor: "R$ 89,90",
  vencimento: "15/10/2026",
  prazo: "em 3 dias",
  link: "https://www.asaas.com/i/exemplo",
  descricao: "Mensalidade rastreamento",
  empresa: "Sua empresa",
};

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const dateBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

function stageLabel(stage: string): string {
  const [kind, n] = stage.split(":");
  if (kind === "before") return `${n} dia${n === "1" ? "" : "s"} antes`;
  if (kind === "due") return "No dia";
  return `${n}ª cobrança após vencer`;
}

function render(template: string) {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k: string) => SAMPLE[k] ?? m);
}

function toForm(s: Settings): Form {
  return {
    enabled: s.enabled,
    beforeDays: s.beforeDays,
    sendOnDueDay: s.sendOnDueDay,
    afterMaxSends: s.afterMaxSends,
    afterIntervalDays: s.afterIntervalDays,
    sendDays: s.sendDays,
    sendTimes: s.sendTimes,
    messageDelaySeconds: s.messageDelaySeconds,
    beforeTemplate: s.beforeTemplate,
    dueTemplate: s.dueTemplate,
    afterTemplate: s.afterTemplate,
  };
}

function errorText(err: unknown, fallback: string) {
  return err instanceof GpsDriveApiError ? err.message : fallback;
}

function InvoiceWhatsappAdminPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["invoice-reminders", "settings"],
    queryFn: () => gpsDriveGet<Settings>(API),
  });
  const [form, setForm] = useState<Form | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (settings.data && !form) setForm(toForm(settings.data));
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: (f: Form) => gpsDrivePut<Settings>(API, f),
    onSuccess: (data) => {
      setSaveError(null);
      setSavedAt(Date.now());
      setForm(toForm(data));
      qc.setQueryData(["invoice-reminders", "settings"], data);
      qc.invalidateQueries({ queryKey: ["invoice-reminders", "preview"] });
    },
    onError: (err) => setSaveError(errorText(err, "Não foi possível salvar.")),
  });

  if (settings.isLoading || !form) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (settings.isError || !settings.data) {
    return (
      <p className="p-6 text-sm text-destructive">Não foi possível carregar a configuração.</p>
    );
  }
  const s = settings.data;
  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(s));

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Envio de Faturas pelo WhatsApp</h1>
          <p className="text-sm text-muted-foreground">
            Lembra o cliente antes do vencimento, no dia e cobra depois de vencida — automático, nos
            dias e horários que você escolher.
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-lg border px-4 py-2">
          <Label htmlFor="ir-enabled" className="text-sm font-medium">
            {form.enabled ? "Envio ligado" : "Envio desligado"}
          </Label>
          <Switch
            id="ir-enabled"
            checked={form.enabled}
            onCheckedChange={(v) => set("enabled", v)}
          />
        </div>
      </div>

      <GpsDriveSessionBanner />

      {(!s.whatsappConnected || !s.asaasConnected) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {!s.whatsappConnected &&
              "O WhatsApp da empresa não está conectado (Administração → Integrações). "}
            {!s.asaasConnected &&
              "A integração com o Asaas não está ativa — sem ela não há faturas para enviar. "}
            Enquanto isso, nada é enviado.
          </span>
        </div>
      )}

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config">Configuração</TabsTrigger>
          <TabsTrigger value="messages">Mensagens</TabsTrigger>
          <TabsTrigger value="preview">Prévia de hoje</TabsTrigger>
          <TabsTrigger value="history">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4 pt-2">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Antes do vencimento</CardTitle>
                <CardDescription>
                  Quantos dias antes avisar. Pode ter mais de um aviso.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NumberChips
                  values={form.beforeDays}
                  onChange={(v) => set("beforeDays", v)}
                  suffix={(n) => `${n} dia${n === 1 ? "" : "s"} antes`}
                  max={5}
                  min={1}
                  limit={30}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">No dia do vencimento</CardTitle>
                <CardDescription>Lembrete no próprio dia em que a fatura vence.</CardDescription>
              </CardHeader>
              <CardContent className="flex items-center gap-3">
                <Switch
                  id="ir-due"
                  checked={form.sendOnDueDay}
                  onCheckedChange={(v) => set("sendOnDueDay", v)}
                />
                <Label htmlFor="ir-due">
                  {form.sendOnDueDay ? "Enviar no dia" : "Não enviar no dia"}
                </Label>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Depois de vencida</CardTitle>
                <CardDescription>Cobranças enquanto a fatura continuar em aberto.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span>Enviar</span>
                  <Input
                    type="number"
                    min={0}
                    max={20}
                    className="h-8 w-16"
                    value={form.afterMaxSends}
                    onChange={(e) =>
                      set("afterMaxSends", Math.max(0, Math.min(20, Number(e.target.value) || 0)))
                    }
                  />
                  <span>vez(es)</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span>a cada</span>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    className="h-8 w-16"
                    value={form.afterIntervalDays}
                    disabled={form.afterMaxSends === 0}
                    onChange={(e) =>
                      set(
                        "afterIntervalDays",
                        Math.max(1, Math.min(60, Number(e.target.value) || 1)),
                      )
                    }
                  />
                  <span>dia(s)</span>
                </div>
                {form.afterMaxSends > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Cobra {afterDaysText(form.afterMaxSends, form.afterIntervalDays)} depois do
                    vencimento.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Dias e horários de envio</CardTitle>
                <CardDescription>
                  Horário de Brasília. Em dia sem envio, o aviso vai no próximo dia permitido.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((d) => {
                    const on = form.sendDays.includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() =>
                          set(
                            "sendDays",
                            on
                              ? form.sendDays.filter((x) => x !== d.value)
                              : [...form.sendDays, d.value],
                          )
                        }
                        className={cn(
                          "h-9 w-12 rounded-md border text-sm font-medium transition-colors",
                          on
                            ? "border-primary bg-primary text-primary-foreground"
                            : "hover:bg-muted",
                        )}
                      >
                        {d.short}
                      </button>
                    );
                  })}
                </div>
                <TimeChips values={form.sendTimes} onChange={(v) => set("sendTimes", v)} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Ritmo de envio</CardTitle>
                <CardDescription>
                  Pausa entre uma mensagem e outra — evita bloqueio do WhatsApp.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Input
                    type="number"
                    min={5}
                    max={600}
                    className="h-8 w-20"
                    value={form.messageDelaySeconds}
                    onChange={(e) =>
                      set(
                        "messageDelaySeconds",
                        Math.max(5, Math.min(600, Number(e.target.value) || 5)),
                      )
                    }
                  />
                  <span>segundos entre mensagens</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Cada fatura recebe no máximo uma mensagem por etapa. Faturas pagas ou excluídas no
                  Asaas são puladas na hora do envio.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="messages" className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">
            Toque numa variável para inserir no texto. Em negrito: *texto*.
          </p>
          {(["before", "due", "after"] as Kind[]).map((kind) => (
            <TemplateCard
              key={kind}
              kind={kind}
              value={form[`${kind}Template` as const]}
              defaultValue={s.defaults[kind]}
              variables={s.variables}
              onChange={(v) => set(`${kind}Template` as const, v)}
            />
          ))}
        </TabsContent>

        <TabsContent value="preview" className="pt-2">
          <PreviewTab dirty={dirty} />
        </TabsContent>

        <TabsContent value="history" className="pt-2">
          <HistoryTab />
        </TabsContent>
      </Tabs>

      <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 border-t bg-background/95 py-3 backdrop-blur">
        {saveError && <span className="text-sm text-destructive">{saveError}</span>}
        {!saveError && savedAt && !dirty && (
          <span className="text-sm text-muted-foreground">Salvo.</span>
        )}
        <Button
          variant="outline"
          disabled={!dirty || save.isPending}
          onClick={() => setForm(toForm(s))}
        >
          Descartar
        </Button>
        <Button
          disabled={!dirty || save.isPending}
          onClick={() => {
            if (form.sendDays.length === 0)
              return setSaveError("Escolha pelo menos um dia da semana.");
            if (form.sendTimes.length === 0) return setSaveError("Informe pelo menos um horário.");
            if (form.beforeDays.length === 0 && !form.sendOnDueDay && form.afterMaxSends === 0)
              return setSaveError("Ative pelo menos um aviso (antes, no dia ou depois).");
            save.mutate(form);
          }}
        >
          {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Salvar
        </Button>
      </div>
    </div>
  );
}

function afterDaysText(max: number, interval: number) {
  const days = Array.from({ length: max }, (_, i) => (i + 1) * interval);
  const shown = days.slice(0, 5).join(", ");
  return `${days.length > 5 ? `${shown}…` : shown} dia(s)`;
}

function NumberChips({
  values,
  onChange,
  suffix,
  max,
  min,
  limit,
}: {
  values: number[];
  onChange: (v: number[]) => void;
  suffix: (n: number) => string;
  max: number;
  min: number;
  limit: number;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < min || n > limit || values.includes(n)) return;
    onChange([...values, n].sort((a, b) => b - a));
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {values.length === 0 && (
          <span className="text-sm text-muted-foreground">Nenhum aviso antes.</span>
        )}
        {values.map((n) => (
          <Badge key={n} variant="secondary" className="gap-1 py-1 pl-2.5 pr-1">
            {suffix(n)}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== n))}
              aria-label="Remover"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      {values.length < max && (
        <div className="flex gap-2">
          <Input
            type="number"
            min={min}
            max={limit}
            placeholder="Dias"
            className="h-8 w-20"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <Button type="button" size="sm" variant="outline" onClick={add}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar
          </Button>
        </div>
      )}
    </div>
  );
}

function TimeChips({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState("09:00");
  const add = () => {
    if (!HHMM.test(draft) || values.includes(draft) || values.length >= 6) return;
    onChange([...values, draft].sort());
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {values.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1 py-1 pl-2.5 pr-1 tabular-nums">
            {t}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== t))}
              aria-label="Remover"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      {values.length < 6 && (
        <div className="flex gap-2">
          <Input
            type="time"
            className="h-8 w-28"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button type="button" size="sm" variant="outline" onClick={add}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Horário
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Com mais de um horário, o segundo só pega o que ficou de fora do primeiro (falha de envio,
        fatura nova) — ninguém recebe a mesma mensagem duas vezes.
      </p>
    </div>
  );
}

function TemplateCard({
  kind,
  value,
  defaultValue,
  variables,
  onChange,
}: {
  kind: Kind;
  value: string;
  defaultValue: string;
  variables: { key: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const test = useMutation({
    mutationFn: () => gpsDrivePost(`${API}/test`, { phone: testPhone, template: value }),
    onSuccess: () => setTestMsg({ ok: true, text: "Enviado." }),
    onError: (err) => setTestMsg({ ok: false, text: errorText(err, "Falha ao enviar o teste.") }),
  });

  const insert = (key: string) => {
    const el = ref.current;
    const token = `{{${key}}}`;
    if (!el) return onChange(value + token);
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">{KIND_LABEL[kind]}</CardTitle>
        </div>
        {value !== defaultValue && (
          <Button variant="ghost" size="sm" onClick={() => onChange(defaultValue)}>
            Restaurar padrão
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <Textarea ref={ref} rows={9} value={value} onChange={(e) => onChange(e.target.value)} />
          <div className="flex flex-wrap gap-1">
            {variables.map((v) => (
              <button
                key={v.key}
                type="button"
                title={v.label}
                onClick={() => insert(v.key)}
                className="rounded border bg-muted/50 px-1.5 py-0.5 font-mono text-[11px] hover:bg-muted"
              >
                {`{{${v.key}}}`}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          <div className="rounded-lg bg-[#e5ddd5] p-3 dark:bg-[#0b141a]">
            <div className="ml-auto max-w-[90%] whitespace-pre-wrap rounded-lg bg-[#dcf8c6] px-3 py-2 text-sm text-slate-900 shadow-sm dark:bg-[#005c4b] dark:text-slate-100">
              {render(value)}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Celular para teste (DDD + número)"
              className="h-8 w-56"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={testPhone.replace(/\D/g, "").length < 10 || test.isPending}
              onClick={() => {
                setTestMsg(null);
                test.mutate();
              }}
            >
              {test.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="mr-1 h-3.5 w-3.5" />
              )}
              Testar
            </Button>
            {testMsg && (
              <span className={cn("text-xs", testMsg.ok ? "text-emerald-600" : "text-destructive")}>
                {testMsg.text}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PreviewTab({ dirty }: { dirty: boolean }) {
  const preview = useQuery({
    queryKey: ["invoice-reminders", "preview"],
    queryFn: () => gpsDriveGet<Preview>(`${API}/preview`),
  });
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="h-4 w-4" /> Quem receberia hoje
        </CardTitle>
        <CardDescription>
          Faturas em aberto que estão numa etapa de envio hoje, pela configuração salva (sem olhar
          dia/horário).
          {dirty && " Salve para ver a prévia com as mudanças."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {preview.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : preview.isError || !preview.data ? (
          <p className="text-sm text-destructive">Não foi possível montar a prévia.</p>
        ) : preview.data.total === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma fatura para enviar hoje.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="secondary">{preview.data.total} fatura(s)</Badge>
              {(["before", "due", "after"] as Kind[]).map(
                (k) =>
                  preview.data!.byKind[k] && (
                    <Badge key={k} variant="outline">
                      {KIND_LABEL[k]}: {preview.data!.byKind[k]}
                    </Badge>
                  ),
              )}
              {preview.data.withoutPhone > 0 && (
                <Badge
                  variant="outline"
                  className="border-amber-500/40 text-amber-700 dark:text-amber-300"
                >
                  {preview.data.withoutPhone} sem celular (não recebem)
                </Badge>
              )}
            </div>
            <InvoiceTable
              rows={preview.data.items.map((i) => ({
                id: i.paymentId,
                customerName: i.customerName,
                phone: i.phone,
                value: i.value,
                dueDate: i.dueDate,
                stage: i.stage,
              }))}
            />
            {preview.data.total > preview.data.items.length && (
              <p className="text-xs text-muted-foreground">
                Mostrando {preview.data.items.length} de {preview.data.total}.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryTab() {
  const history = useQuery({
    queryKey: ["invoice-reminders", "history"],
    queryFn: () => gpsDriveGet<HistoryRow[]>(`${API}/history`),
    refetchInterval: 60_000,
  });
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Últimos envios</CardTitle>
        <CardDescription>As 100 mensagens mais recentes.</CardDescription>
      </CardHeader>
      <CardContent>
        {history.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : !history.data?.length ? (
          <p className="text-sm text-muted-foreground">Nenhum envio ainda.</p>
        ) : (
          <InvoiceTable
            rows={history.data.map((h) => ({
              id: h.id,
              customerName: h.customerName ?? "—",
              phone: h.phone,
              value: h.value,
              dueDate: h.dueDate,
              stage: h.stage,
              status: h.status,
              error: h.errorMessage,
              at: h.sentAt,
            }))}
          />
        )}
      </CardContent>
    </Card>
  );
}

function InvoiceTable({
  rows,
}: {
  rows: {
    id: string;
    customerName: string;
    phone: string | null;
    value: number | null;
    dueDate: string | null;
    stage: string;
    status?: HistoryRow["status"];
    error?: string | null;
    at?: string;
  }[];
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            {rows[0]?.at && <th className="px-3 py-2 font-medium">Enviado</th>}
            <th className="px-3 py-2 font-medium">Cliente</th>
            <th className="px-3 py-2 font-medium">Celular</th>
            <th className="px-3 py-2 font-medium">Valor</th>
            <th className="px-3 py-2 font-medium">Vencimento</th>
            <th className="px-3 py-2 font-medium">Etapa</th>
            {rows[0]?.status && <th className="px-3 py-2 font-medium">Situação</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => (
            <tr key={r.id}>
              {r.at && (
                <td className="whitespace-nowrap px-3 py-2 text-xs">
                  {new Date(r.at).toLocaleString("pt-BR")}
                </td>
              )}
              <td className="px-3 py-2">{r.customerName}</td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                {r.phone ?? <span className="text-amber-600">sem celular</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                {r.value != null ? brl.format(r.value) : "—"}
              </td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                {r.dueDate ? dateBr(r.dueDate) : "—"}
              </td>
              <td className="whitespace-nowrap px-3 py-2">{stageLabel(r.stage)}</td>
              {r.status && (
                <td className="whitespace-nowrap px-3 py-2">
                  {r.status === "sent" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Enviado
                    </span>
                  ) : r.status === "no_phone" ? (
                    <span className="text-amber-600">Sem celular</span>
                  ) : (
                    <span className="text-destructive" title={r.error ?? ""}>
                      Falhou
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
